import { graphql } from "@acto/gql";
import { ClientFilter, ClientOrderBy, ClientSearchMode, LastVisitBucket } from "@acto/gql/graphql";
import { useMemo } from "react";
import { CustomerListItem, CustomersTotals } from "@acto/taskpane/core/domain";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { useComparisonModeContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { toGqlKpiComparisonMode } from "@acto/taskpane/core/data-access/customers/kpiTransformers";

const CustomersManagementQuery = graphql(`
  query GetCustomersManagement(
    $first: Int!
    $after: String
    $filter: ClientFilter
    $dateRange: DateRangeInput!
    $comparisonMode: KpiComparisonMode!
  ) {
    clients(first: $first, after: $after, filter: $filter) {
      edges {
        node {
          id
          name
          assignedEmployee {
            name
          }
          lastVisitDate
          meanDaysBetweenOrders(dateRange: $dateRange) {
            value
          }
          revenue(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            totalValue
            percentageChange
          }
          profit(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            totalValue
            percentageChange
          }
          margin(dateRange: $dateRange, comparisonMode: $comparisonMode) {
            totalValue
            percentageChange
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totals {
        revenue {
          totalValue
          previousTotalValue
          percentageChange
        }
        profit {
          totalValue
          previousTotalValue
          percentageChange
        }
        dateRanges {
          previous {
            startDate
            endDate
          }
        }
      }
    }
  }
`);

interface ClientNodeWithKpis {
  id: string;
  name: string;
  revenue: { totalValue: number; percentageChange?: number | null } | null;
  profit: { totalValue: number; percentageChange?: number | null } | null;
  margin: { totalValue: number; percentageChange?: number | null } | null;
  assignedEmployeeName: string | null;
  lastVisitDate: string | null;
  meanDaysBetweenOrders: number | null;
}

export interface RevenueRange {
  min: number | null;
  max: number | null;
}

export interface LastVisitRange {
  startDate: string | null;
  endDate: string | null;
}

interface UseCustomersManagementOptions {
  dateRange: { startDate: string; endDate: string };
  pageSize?: number;
  orderBy?: ClientOrderBy;
  searchTerm?: string;
  assignedEmployeeId?: string | null;
  revenueRange?: RevenueRange;
  lastVisitBucket?: LastVisitBucket | null;
  lastVisitRange?: LastVisitRange;
}

export interface PreviousDateRange {
  startDate: string;
  endDate: string;
}

interface CustomersMetadata {
  totals: CustomersTotals;
  previousRange?: PreviousDateRange;
}

export type UseCustomersManagementResult = PaginatedDataAccessResult<CustomerListItem, CustomersMetadata> & {
  totals?: CustomersTotals;
  previousRange?: PreviousDateRange;
};

const DEFAULT_PAGE_SIZE = 10;

export function useCustomersManagement({
  dateRange,
  pageSize = DEFAULT_PAGE_SIZE,
  orderBy,
  searchTerm,
  assignedEmployeeId,
  revenueRange,
  lastVisitBucket,
  lastVisitRange,
}: UseCustomersManagementOptions): UseCustomersManagementResult {
  const comparisonMode = toGqlKpiComparisonMode(useComparisonModeContext());

  // dateRange in filter drives sorting/totals in ClientsController; top-level dateRange drives sub-field KPIs
  const filter: ClientFilter | undefined = useMemo(() => {
    const hasRevenueBound = revenueRange != null && (revenueRange.min != null || revenueRange.max != null);
    const hasLastVisitRange =
      lastVisitRange != null && (lastVisitRange.startDate != null || lastVisitRange.endDate != null);

    return {
      orderBy,
      searchTerm: searchTerm || undefined,
      searchMode: ClientSearchMode.Smart,
      dateRange,
      comparisonMode,
      assignedEmployeeId: assignedEmployeeId || undefined,
      revenueRange: hasRevenueBound ? { min: revenueRange.min, max: revenueRange.max } : undefined,
      // Backend accepts either a bucket or a custom range; the page UI ensures they're mutually exclusive.
      lastVisitBucket: hasLastVisitRange ? undefined : (lastVisitBucket ?? undefined),
      lastVisitRange: hasLastVisitRange
        ? { startDate: lastVisitRange.startDate, endDate: lastVisitRange.endDate }
        : undefined,
    };
  }, [
    orderBy,
    searchTerm,
    dateRange,
    comparisonMode,
    assignedEmployeeId,
    revenueRange,
    lastVisitBucket,
    lastVisitRange,
  ]);

  const variables = useMemo(() => ({ filter, dateRange, comparisonMode }), [filter, dateRange, comparisonMode]);

  const result = usePagination({
    query: CustomersManagementQuery,
    limit: pageSize,
    variables,
    connection: (data) => ({
      edges: data.clients.edges.map((edge) => {
        return {
          node: {
            id: edge.node.id,
            name: edge.node.name,
            revenue: edge.node.revenue ?? null,
            profit: edge.node.profit ?? null,
            margin: edge.node.margin ?? null,
            assignedEmployeeName: edge.node.assignedEmployee?.name ?? null,
            lastVisitDate: edge.node.lastVisitDate ?? null,
            meanDaysBetweenOrders: edge.node.meanDaysBetweenOrders?.value ?? null,
          } as ClientNodeWithKpis,
        };
      }),
      pageInfo: data.clients.pageInfo,
    }),
    metadata: (data) => {
      if (!data.clients.totals) return undefined;
      return {
        totals: CustomersTotals.fromTotals(data.clients.totals),
        previousRange: data.clients.totals.dateRanges?.previous ?? undefined,
      };
    },
  });

  const customers = useMemo(
    () =>
      (result.data ?? []).map((node) =>
        CustomerListItem.fromEdge({
          node: { id: node.id, name: node.name },
          revenue: node.revenue,
          profit: node.profit,
          margin: node.margin,
          assignedEmployeeName: node.assignedEmployeeName,
          lastVisitDate: node.lastVisitDate,
          meanDaysBetweenOrders: node.meanDaysBetweenOrders,
        }),
      ),
    [result.data],
  );

  return {
    ...result,
    data: customers,
    totals: result.metadata?.totals,
    previousRange: result.metadata?.previousRange,
  } as UseCustomersManagementResult;
}
